import { Screen, ScreenConfig } from '../core/Screen';
import { APIManager, StateManager } from '../../managers';
import { rarityColor, buildItemTooltip } from '../components/TooltipContent';
import { renderItemIcon, getCurrencyIconUrl } from '../components/ItemIcon';
import { screenHeaderHTML, renderScreenCurrencies } from '../components/ScreenHeader';
import { Tooltip } from '../components/Tooltip';
import { getFullSpriteUrl, CB, PLAYER_SPRITES } from '../../combat/spriteConfig';
import type {
  PlayerData,
  ConfigData,
  ShopListingData,
  ItemData,
  ActiveEventBundle,
} from '../../types';

const SHOP_BG_URL = `${import.meta.env.BASE_URL}sprites/processed/hub_bg/the_greyhand_v5.webp${CB}`;
const KESSIN_SPRITE_URL = `${import.meta.env.BASE_URL}sprites/processed/players/kessin_shopkeeper_v3.webp`;
const PEDESTAL_SPRITE_URL = `${import.meta.env.BASE_URL}sprites/processed/props/display_pedestal.webp`;

/** ms the ware tile burst plays before the listing is removed from state.
 *  Match the longest .shop-ware-tile.just-sold animation. */
const WARE_BURST_DURATION_MS = 650;

/** Featured listing → which sprite path to resolve in renderAlcove. */
function kindOf(listing: ShopListingData | undefined): 'strand' | 'skin' {
  return listing?.listingType === 'unit' ? 'strand' : 'skin';
}

export class ShopScreen extends Screen {
  readonly screenConfig: ScreenConfig = {
    id: 'shop',
    name: 'Shop',
    persistent: false,
    needsPhaser: false,
  };

  private activeEvent: ActiveEventBundle | null = null;
  /** Listing IDs whose plaque should play the SOLD pop on the next render. */
  private justSoldIds = new Set<string>();
  private waresOpen = false;
  /** ESC handler bound while the wares overlay is open. */
  private waresEscHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor() {
    super({ rootId: 'shop-screen-root' });
  }

  createDOM(container: HTMLElement): void {
    const root = document.createElement('div');
    root.id = 'shop-screen-root';
    root.innerHTML = `
      <div class="shop-screen">
        ${screenHeaderHTML('Shop')}
        <div class="shop-body">
          <div class="shop-stage-wrap">
            <div class="shop-stage">
              <div class="shop-bg" style="background-image:url('${SHOP_BG_URL}')"></div>

              <!-- Per-event ambient color wash. CSS reads --event-accent set on .shop-stage. -->
              <div class="shop-event-tint"></div>

              <!-- Dust motes — only visible when an event bg is active.
                   Absolute-positioned dots with infinite drift animations. -->
              <div class="shop-event-particles" aria-hidden="true">
                <span class="shop-particle shop-particle--1"></span>
                <span class="shop-particle shop-particle--2"></span>
                <span class="shop-particle shop-particle--3"></span>
                <span class="shop-particle shop-particle--4"></span>
                <span class="shop-particle shop-particle--5"></span>
                <span class="shop-particle shop-particle--6"></span>
              </div>

              <!-- Three soft dark vignettes behind the alcove positions — pushes the wall
                   back so grey/low-contrast skins (e.g. Surfernaut) read against it. -->
              <div class="shop-spotlights"></div>

              <!-- Three foreground pedestal props planted on the polished wood floor. -->
              <img class="shop-pedestal-prop shop-pedestal-prop--left"   src="${PEDESTAL_SPRITE_URL}" alt="" draggable="false">
              <img class="shop-pedestal-prop shop-pedestal-prop--center" src="${PEDESTAL_SPRITE_URL}" alt="" draggable="false">
              <img class="shop-pedestal-prop shop-pedestal-prop--right"  src="${PEDESTAL_SPRITE_URL}" alt="" draggable="false">

              <!-- Three display alcoves — center is the headliner (strand), flanks are skins. -->
              <div class="shop-alcove shop-alcove--left">
                <div class="shop-alcove-sprite-slot"></div>
                <div class="shop-alcove-plaque hidden"></div>
              </div>

              <div class="shop-alcove shop-alcove--center">
                <div class="shop-alcove-sprite-slot"></div>
                <div class="shop-alcove-plaque hidden"></div>
              </div>

              <div class="shop-alcove shop-alcove--right">
                <div class="shop-alcove-sprite-slot"></div>
                <div class="shop-alcove-plaque hidden"></div>
              </div>

              <!-- Bundle title overlay — top-center, no painted sign to collide with. -->
              <div class="shop-bundle-banner hidden"></div>

              <!-- Kessin — Caligari shopkeeper, free-standing on the right side of the floor.
                   Tap to open the wares modal. The plaque is always-visible so Kessin
                   reads as the same kind of interactable as the alcove trio. -->
              <div class="shop-keeper" role="button" tabindex="0" aria-label="Talk to Kessin to browse merchant's wares">
                <div class="shop-keeper-glow"></div>
                <img class="shop-keeper-sprite" src="${KESSIN_SPRITE_URL}" alt="" draggable="false">
                <div class="shop-keeper-plaque">
                  <span class="shop-keeper-plaque-label">Wares</span>
                  <span class="shop-keeper-plaque-count hidden">0</span>
                </div>
              </div>

              <div class="shop-burst-layer"></div>
              <div class="shop-featured-empty hidden">No featured bundle today.</div>
            </div>
          </div>

          <div class="shop-loading">Loading shop...</div>
        </div>

        <!-- Wares flyout — opened by tapping Kessin (right-side slide-in,
             reuses the .roster-overlay shell from main-menu.css). -->
        <div class="roster-overlay shop-wares-overlay" role="dialog" aria-modal="true" aria-label="Merchant's Wares">
          <div class="roster-overlay-backdrop"></div>
          <div class="roster-overlay-panel">
            <div class="roster-overlay-header">
              <span class="roster-overlay-title">Merchant's Wares <span class="shop-wares-count hidden"></span></span>
              <button class="roster-overlay-close" type="button" aria-label="Close">×</button>
            </div>
            <div class="shop-wares-grid"></div>
            <div class="shop-wares-empty hidden">Wares restock tomorrow.</div>
          </div>
        </div>
      </div>
    `;
    container.appendChild(root);
  }

  protected cacheElements(): void {}

  protected bindEvents(): void {
    const keeper = this.$('.shop-keeper');
    if (keeper) {
      keeper.addEventListener('click', () => this.openWares());
      keeper.addEventListener('keydown', (e) => {
        const ev = e as KeyboardEvent;
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          this.openWares();
        }
      });
    }

    // Hover-to-purchase: clicking any alcove buys its featured listing.
    // Sold and unaffordable alcoves are skipped (no event fires).
    this.rootEl?.querySelectorAll<HTMLElement>('.shop-alcove').forEach((alcove) => {
      const tryBuy = () => {
        if (alcove.classList.contains('shop-alcove--empty')) return;
        if (alcove.classList.contains('shop-alcove--sold')) return;
        if (alcove.classList.contains('shop-alcove--unaffordable')) return;
        const id = alcove.dataset.listingId;
        if (id) this.handleBuy(id);
      };
      alcove.addEventListener('click', tryBuy);
      alcove.addEventListener('keydown', (e) => {
        const ev = e as KeyboardEvent;
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          tryBuy();
        }
      });
    });

    this.$('.shop-wares-overlay .roster-overlay-close')?.addEventListener('click', () => this.closeWares());
    this.$('.shop-wares-overlay .roster-overlay-backdrop')?.addEventListener('click', () => this.closeWares());
  }

  protected setupSubscriptions(): void {
    this.subscribeState('playerData', () => this.renderCurrencies());
    this.subscribeState('shopData', () => this.renderListings());
  }

  async onEnter(): Promise<void> {
    this.$('.shop-loading')?.classList.remove('hidden');
    this.resetUI();
    this.closeWares();

    this.onRender();

    const result = await APIManager.getShop();
    if (result.success && result.data) {
      this.activeEvent = result.data.activeEvent ?? null;
      StateManager.set('shopData', result.data.listings);
    }
  }

  onLeave(): void {
    this.closeWares();
  }

  protected onRender(): void {
    this.renderCurrencies();
    this.renderListings();
  }

  private resetUI(): void {
    this.$('.shop-bundle-banner')?.classList.add('hidden');
    this.$('.shop-featured-empty')?.classList.add('hidden');
    this.rootEl?.querySelectorAll('.shop-alcove').forEach(el => { el.classList.add('shop-alcove--empty'); });
    this.rootEl?.querySelectorAll('.shop-alcove-sprite-slot').forEach(el => { el.innerHTML = ''; });
    this.rootEl?.querySelectorAll('.shop-alcove-plaque').forEach(el => {
      el.innerHTML = '';
      el.classList.add('hidden');
    });
    const burst = this.$('.shop-burst-layer');
    if (burst) burst.innerHTML = '';
    const grid = this.$('.shop-wares-grid');
    if (grid) grid.innerHTML = '';
  }

  private renderCurrencies(): void {
    if (!this.rootEl) return;
    // Shop is gold-only — no soulthread/spools/etc. clutter in the wallet.
    renderScreenCurrencies(this.rootEl, 'shop', ['gold']);
  }

  // ================================================================
  // Rendering
  // ================================================================

  private renderListings(): void {
    const listings = StateManager.get('shopData') as ShopListingData[] | undefined;
    if (!listings) return;

    this.$('.shop-loading')?.classList.add('hidden');

    const player = StateManager.get('playerData') as PlayerData | undefined;
    const configData = StateManager.get('configData') as ConfigData | undefined;
    const playerGold = player?.gold ?? 0;

    const accentColor = this.activeEvent?.accentColor ?? '#c8a86e';
    const stage = this.$('.shop-stage') as HTMLElement | null;
    if (stage) stage.style.setProperty('--event-accent', accentColor);

    // Per-event shop bg — falls back to default if no event bg set.
    const bgEl = this.$('.shop-bg') as HTMLElement | null;
    const eventBg = this.activeEvent?.shopBgUrl;
    if (bgEl) {
      const url = eventBg
        ? `${import.meta.env.BASE_URL}${eventBg}${CB}`
        : SHOP_BG_URL;
      bgEl.style.backgroundImage = `url('${url}')`;
    }
    // Stage-level flag for event-bg-scoped CSS overrides (hides wooden
    // pedestals + dark spotlights, adds accent-colored sprite halos).
    if (stage) {
      if (eventBg) stage.setAttribute('data-event-bg', '');
      else stage.removeAttribute('data-event-bg');
    }

    // Featured listings = anything sourced from the active event that's a
    // strand or skin (not items — those live in the wares flyout).
    // Headliner pick: prefer a strand; otherwise first skin in slot order.
    const featured = listings.filter(l =>
      l.source === 'event' && (l.listingType === 'unit' || l.listingType === 'skin'),
    );
    const headliner = featured.find(l => l.listingType === 'unit') ?? featured[0];
    const flanks = featured.filter(l => l !== headliner);

    this.renderAlcoves(headliner, flanks, playerGold, configData);
    this.renderBundleBanner(headliner, configData);
    this.renderFeaturedEmpty(headliner);

    const wareListings = listings.filter(l =>
      l.listingType !== 'unit' && l.listingType !== 'skin'
    );
    this.renderWaresModal(wareListings, playerGold, configData);
    this.updateKeeperEmptyState(wareListings);
  }

  private renderFeaturedEmpty(headliner: ShopListingData | undefined): void {
    const empty = this.$('.shop-featured-empty');
    if (!empty) return;
    empty.classList.toggle('hidden', !!headliner);
  }

  /** When Kessin has nothing to sell, dim him + hide the plaque so the
   *  player isn't tricked into clicking an empty flyout. Otherwise update
   *  the count chip on the plaque. */
  private updateKeeperEmptyState(wareListings: ShopListingData[]): void {
    const available = wareListings.filter(l => !l.purchased).length;
    const keeper = this.$('.shop-keeper') as HTMLElement | null;
    if (keeper) keeper.classList.toggle('shop-keeper--empty', available === 0);

    const count = this.$('.shop-keeper-plaque-count') as HTMLElement | null;
    if (count) {
      if (available > 0) {
        count.textContent = String(available);
        count.classList.remove('hidden');
      } else {
        count.classList.add('hidden');
      }
    }
  }

  // ── Alcoves (left, center = headliner, right) ─

  private renderAlcoves(
    headliner: ShopListingData | undefined,
    flanks: ShopListingData[],
    playerGold: number,
    configData: ConfigData | undefined,
  ): void {
    this.renderAlcove('center', headliner, kindOf(headliner), playerGold, configData);
    this.renderAlcove('left',   flanks[0], kindOf(flanks[0]), playerGold, configData);
    this.renderAlcove('right',  flanks[1], kindOf(flanks[1]), playerGold, configData);
  }

  private renderAlcove(
    slotPos: 'left' | 'center' | 'right',
    listing: ShopListingData | undefined,
    listingKind: 'strand' | 'skin',
    playerGold: number,
    configData: ConfigData | undefined,
  ): void {
    const alcove = this.$(`.shop-alcove--${slotPos}`) as HTMLElement | null;
    if (!alcove) return;

    const slot = alcove.querySelector<HTMLElement>('.shop-alcove-sprite-slot');
    const plaque = alcove.querySelector<HTMLElement>('.shop-alcove-plaque');
    if (!slot || !plaque) return;

    if (!listing) {
      slot.innerHTML = '';
      plaque.innerHTML = '';
      plaque.classList.add('hidden');
      alcove.classList.add('shop-alcove--empty');
      delete alcove.dataset.listingId;
      return;
    }
    alcove.classList.remove('shop-alcove--empty');
    alcove.dataset.listingId = listing.id;

    // Sprite — resolve URL + per-sprite scale tuning from spriteConfig.
    const data = this.parseEntity(listing);
    let spriteUrl = '';
    let spriteKey: string | undefined;
    if (listingKind === 'strand') {
      const unitClass = data.unitClass ?? '';
      spriteKey = unitClass ? configData?.unitTemplates?.[unitClass]?.spriteKey : undefined;
      if (spriteKey) spriteUrl = getFullSpriteUrl(spriteKey);
    } else {
      const skin = data.skinId ? configData?.skins?.[data.skinId as string] : undefined;
      if (skin) {
        spriteKey = skin.spriteKey;
        spriteUrl = getFullSpriteUrl(skin.spriteKey);
      }
    }

    // Apply per-sprite scale (e.g. Granocen at 1.15) so race-canonical heights
    // hold across the alcove showcase. We set --sprite-scale as a CSS variable
    // on the slot, which CSS multiplies into the sprite's height — that keeps
    // the layout box accurate so the plaque (positioned `bottom: 100%`) hangs
    // above the actual sprite top, not above an unscaled phantom box.
    const scale = spriteKey ? PLAYER_SPRITES[spriteKey]?.scale ?? 1 : 1;
    slot.style.setProperty('--sprite-scale', scale.toString());

    slot.innerHTML = spriteUrl
      ? `<img class="shop-alcove-sprite shop-alcove-sprite--${slotPos}" src="${spriteUrl}" alt="" draggable="false">`
      : '';

    // Plaque (static name + price label — no button; the sprite IS the buy target)
    plaque.classList.remove('hidden');
    plaque.innerHTML = this.renderAlcovePlaque(listing, listingKind, playerGold);

    // Hover-to-purchase: clicking the sprite (or anywhere on the alcove) buys.
    const canAfford = playerGold >= listing.price;
    alcove.classList.toggle('shop-alcove--unaffordable', !canAfford && !listing.purchased);
    alcove.classList.toggle('shop-alcove--sold', !!listing.purchased);
    alcove.tabIndex = listing.purchased || !canAfford ? -1 : 0;
    alcove.setAttribute('role', 'button');
    const verb = listingKind === 'strand' ? 'Recruit' : 'Buy';
    alcove.setAttribute(
      'aria-label',
      listing.purchased
        ? `${listing.displayName} sold`
        : `${verb} ${listing.displayName} for ${listing.price} gold`,
    );
  }

  private renderAlcovePlaque(
    listing: ShopListingData,
    listingKind: 'strand' | 'skin',
    playerGold: number,
  ): string {
    const canAfford = playerGold >= listing.price;
    const justSold = this.justSoldIds.has(listing.id);
    const stateClasses = [
      listing.purchased ? 'is-sold' : '',
      justSold ? 'just-sold' : '',
      !canAfford && !listing.purchased ? 'is-unaffordable' : '',
    ].filter(Boolean).join(' ');

    const typeLabel = listingKind === 'strand' ? 'Strand' : 'Skin';

    if (listing.purchased) {
      return `
        <div class="shop-alcove-plaque-inner ${stateClasses}">
          <span class="shop-alcove-type">★ ${typeLabel}</span>
          <span class="shop-alcove-name">${this.escape(listing.displayName)}</span>
          <span class="shop-alcove-sold">SOLD</span>
        </div>
      `;
    }

    const goldIconUrl = getCurrencyIconUrl('gold');
    return `
      <div class="shop-alcove-plaque-inner ${stateClasses}">
        <span class="shop-alcove-type">★ ${typeLabel}</span>
        <span class="shop-alcove-name">${this.escape(listing.displayName)}</span>
        <span class="shop-alcove-price${!canAfford ? ' too-expensive' : ''}">${listing.price}<img class="shop-alcove-price-coin" src="${goldIconUrl}" alt="" draggable="false"></span>
      </div>
    `;
  }

  // ── Bundle banner — just the event name. The 3 individual featured
  //   listings each carry their own plaque, so a single class·race·element
  //   subheader for "the bundle" is misleading when the trio spans
  //   different strands.

  private renderBundleBanner(
    headliner: ShopListingData | undefined,
    _configData: ConfigData | undefined,
  ): void {
    const banner = this.$('.shop-bundle-banner');
    if (!banner) return;

    if (!headliner) {
      banner.classList.add('hidden');
      banner.innerHTML = '';
      return;
    }

    const bundleName = this.activeEvent?.name ?? headliner.displayName;
    const subtitle = this.activeEvent?.subtitle;

    banner.classList.remove('hidden');
    banner.innerHTML = `
      <div class="shop-bundle-banner-tag">★ Event Banner</div>
      <h2 class="shop-bundle-banner-title">${this.escape(bundleName)}</h2>
      ${subtitle ? `<p class="shop-bundle-banner-subtitle">${this.escape(subtitle)}</p>` : ''}
    `;
  }

  // ── Wares modal ──────────────────────────────────────────────────

  private openWares(): void {
    if (this.waresOpen) return;
    this.waresOpen = true;
    this.$('.shop-wares-overlay')?.classList.add('is-visible');

    this.waresEscHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.closeWares();
    };
    document.addEventListener('keydown', this.waresEscHandler);
  }

  private closeWares(): void {
    if (!this.waresOpen) return;
    this.waresOpen = false;
    this.$('.shop-wares-overlay')?.classList.remove('is-visible');
    Tooltip.hide();

    if (this.waresEscHandler) {
      document.removeEventListener('keydown', this.waresEscHandler);
      this.waresEscHandler = null;
    }
  }

  private renderWaresModal(
    listings: ShopListingData[],
    playerGold: number,
    configData?: ConfigData,
  ): void {
    const grid = this.$('.shop-wares-grid') as HTMLElement | null;
    const empty = this.$('.shop-wares-empty');
    const count = this.$('.shop-wares-count') as HTMLElement | null;
    if (!grid) return;

    const available = listings.filter(l => !l.purchased);

    if (count) {
      if (available.length > 0) {
        count.textContent = String(available.length);
        count.classList.remove('hidden');
      } else {
        count.classList.add('hidden');
      }
    }

    if (available.length === 0) {
      grid.innerHTML = '';
      empty?.classList.remove('hidden');
      if (this.waresOpen) {
        setTimeout(() => this.closeWares(), 800);
      }
      return;
    }
    empty?.classList.add('hidden');

    grid.innerHTML = available
      .map(l => this.renderWareTile(l, playerGold, configData))
      .join('');

    grid.querySelectorAll<HTMLButtonElement>('.shop-ware-tile').forEach(btn => {
      const listingId = btn.dataset.listingId!;
      const listing = listings.find(l => l.id === listingId);
      if (!listing) return;

      if (listing.listingType === 'item') {
        const itemData = this.listingToItemData(listing);
        if (itemData) {
          Tooltip.attach(btn, () => buildItemTooltip(itemData, configData));
        }
      }

      btn.addEventListener('click', () => this.handleBuy(listingId, btn));
    });
  }

  private renderWareTile(
    listing: ShopListingData,
    playerGold: number,
    _configData?: ConfigData,
  ): string {
    const canAfford = playerGold >= listing.price;
    const data = this.parseEntity(listing);
    const rColor = rarityColor(listing.rarity || 'normal');
    const iconHTML = renderItemIcon(data.baseType ?? '', data.category ?? 'weapon', 'md');

    return `
      <button
        class="shop-ware-tile"
        type="button"
        data-listing-id="${listing.id}"
        data-rarity="${listing.rarity || 'normal'}"
        style="--rarity-color:${rColor}"
        ${!canAfford ? 'disabled' : ''}
        aria-label="Buy ${this.escape(listing.displayName)} for ${listing.price} gold"
      >
        <span class="shop-ware-tile-icon">
          ${iconHTML}
        </span>
        <span class="shop-ware-tile-name">${this.escape(listing.displayName)}</span>
        <span class="shop-ware-tile-price${!canAfford ? ' too-expensive' : ''}">${listing.price}<img class="shop-ware-tile-price-coin" src="${getCurrencyIconUrl('gold')}" alt="" draggable="false"></span>
      </button>
    `;
  }

  // ================================================================
  // Helpers
  // ================================================================

  private parseEntity(listing: ShopListingData): Record<string, unknown> & {
    unitClass?: string;
    race?: string;
    skinId?: string;
    baseType?: string;
    category?: string;
  } {
    try {
      return JSON.parse(listing.entityData);
    } catch {
      return {};
    }
  }

  /** Convert a shop item listing into the ItemData shape buildItemTooltip
   *  expects. Synthetic id/playerId — they're never read by the tooltip. */
  private listingToItemData(listing: ShopListingData): ItemData | null {
    if (listing.listingType !== 'item') return null;
    const data = this.parseEntity(listing);
    return {
      id: `shop-${listing.id}`,
      playerId: '',
      category: (data.category as ItemData['category']) ?? 'weapon',
      baseType: (data.baseType as string) ?? '',
      name: listing.displayName,
      level: listing.level,
      rarity: listing.rarity || 'normal',
      implicitAffix: typeof data.implicitAffix === 'string'
        ? data.implicitAffix
        : (data.implicitAffix ? JSON.stringify(data.implicitAffix) : ''),
      explicitAffixes: typeof data.explicitAffixes === 'string'
        ? data.explicitAffixes
        : (data.explicitAffixes ? JSON.stringify(data.explicitAffixes) : ''),
      equippedToUnitId: null,
      inventorySlot: null,
      requirementsDisplay: '',
    };
  }

  private escape(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // ================================================================
  // Buy flow + celebration
  // ================================================================

  private async handleBuy(listingId: string, sourceEl?: HTMLElement): Promise<void> {
    const preListings = StateManager.get('shopData') as ShopListingData[] | undefined;
    const listing = preListings?.find(l => l.id === listingId);

    const result = await APIManager.buyShopListing(listingId);

    if (!result.success) {
      console.error('[ShopScreen] Purchase failed:', result.error);
      const { ToastManager } = await import('../components/Toast');
      ToastManager.show({ type: 'error', message: result.error ?? 'Purchase failed' });
      return;
    }

    // Refresh full state so inventory / currencies / unit roster update.
    const stateResult = await APIManager.fetchUnifiedState();
    if (stateResult.success && stateResult.data) {
      StateManager.batchUpdate({
        playerData: stateResult.data.playerData,
        unitData: stateResult.data.unitData,
        itemData: stateResult.data.itemData,
      });
    }

    if (!listing) return;

    const isFeatured = listing.listingType === 'unit' || listing.listingType === 'skin';
    void this.showAcquiredToast(listing);

    if (isFeatured) {
      this.justSoldIds.add(listingId);
      setTimeout(() => this.justSoldIds.delete(listingId), 1200);

      this.playFeaturedBurst(listing);

      const shopData = StateManager.get('shopData') as ShopListingData[] | undefined;
      if (shopData) {
        const updated = shopData.map(l =>
          l.id === listingId ? { ...l, purchased: true } : l,
        );
        StateManager.set('shopData', updated);
      }
    } else if (sourceEl) {
      sourceEl.classList.add('just-sold');
      this.playWareBurst(sourceEl);
      Tooltip.hide();

      await new Promise(resolve => setTimeout(resolve, WARE_BURST_DURATION_MS));

      const shopData = StateManager.get('shopData') as ShopListingData[] | undefined;
      if (shopData) {
        const updated = shopData.map(l =>
          l.id === listingId ? { ...l, purchased: true } : l,
        );
        StateManager.set('shopData', updated);
      }
    }
  }

  private async showAcquiredToast(listing: ShopListingData): Promise<void> {
    const { ToastManager } = await import('../components/Toast');
    ToastManager.show({
      type: 'success',
      message: `Acquired: ${listing.displayName}`,
      duration: 2400,
    });
  }

  /** Strand/skin purchase — burst originates from the matching alcove sprite. */
  private playFeaturedBurst(listing: ShopListingData): void {
    const layer = this.$('.shop-burst-layer') as HTMLElement | null;
    const stage = this.$('.shop-stage') as HTMLElement | null;
    if (!layer || !stage) return;

    // Find the alcove that owns this listing, then its sprite.
    const alcove = this.rootEl?.querySelector<HTMLElement>(
      `.shop-alcove[data-listing-id="${listing.id}"]`,
    );
    const sprite = alcove?.querySelector<HTMLElement>('.shop-alcove-sprite') ?? null;

    if (sprite) {
      sprite.classList.remove('shop-claimed');
      void sprite.offsetWidth;
      sprite.classList.add('shop-claimed');
      setTimeout(() => sprite.classList.remove('shop-claimed'), 950);
    }

    if (!sprite) return;

    const stageRect = stage.getBoundingClientRect();
    const spriteRect = sprite.getBoundingClientRect();
    if (stageRect.width === 0 || stageRect.height === 0) return;

    const cx = ((spriteRect.left + spriteRect.width / 2 - stageRect.left) / stageRect.width) * 100;
    const cy = ((spriteRect.top + spriteRect.height * 0.4 - stageRect.top) / stageRect.height) * 100;

    const ring = document.createElement('div');
    ring.className = 'shop-burst-ring';
    ring.style.left = `${cx}%`;
    ring.style.top = `${cy}%`;
    layer.appendChild(ring);
    setTimeout(() => ring.remove(), 900);

    for (let i = 0; i < 14; i++) {
      const spark = document.createElement('div');
      spark.className = 'shop-spark';
      spark.style.left = `${cx}%`;
      spark.style.top = `${cy}%`;
      const angle = (i / 14) * 360 + Math.random() * 14;
      const distance = 160 + Math.random() * 80;
      spark.style.setProperty('--shop-spark-angle', `${angle}deg`);
      spark.style.setProperty('--shop-spark-distance', `${distance}px`);
      spark.style.animationDelay = `${Math.random() * 90}ms`;
      layer.appendChild(spark);
    }
    setTimeout(() => layer.querySelectorAll('.shop-spark').forEach(s => s.remove()), 1100);

    const ribbon = document.createElement('div');
    ribbon.className = 'shop-acquired-ribbon';
    ribbon.textContent = 'Acquired';
    ribbon.style.left = `${cx}%`;
    ribbon.style.top = `${Math.max(8, cy - 26)}%`;
    layer.appendChild(ribbon);
    setTimeout(() => ribbon.remove(), 1500);
  }

  /** Ware purchase — ring + "+1 ITEM" floater spawn over the source tile. */
  private playWareBurst(tile: HTMLElement): void {
    const grid = tile.parentElement;
    if (!grid) return;

    const tileRect = tile.getBoundingClientRect();
    const gridRect = grid.getBoundingClientRect();
    const left = tileRect.left + tileRect.width / 2 - gridRect.left;
    const top = tileRect.top + tileRect.height / 2 - gridRect.top;

    const tileRarityColor = tile.style.getPropertyValue('--rarity-color') || '#ffd66e';
    const ring = document.createElement('div');
    ring.className = 'shop-ware-ring';
    ring.style.left = `${left}px`;
    ring.style.top = `${top}px`;
    ring.style.setProperty('--ware-ring-color', tileRarityColor);
    grid.appendChild(ring);
    setTimeout(() => ring.remove(), 700);

    const float = document.createElement('div');
    float.className = 'shop-ware-floater';
    float.textContent = '+1 ITEM';
    float.style.left = `${left}px`;
    float.style.top = `${top}px`;
    grid.appendChild(float);
    setTimeout(() => float.remove(), 900);
  }
}
